import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { useTranslation } from '../hooks/useTranslation';

export default function Privacy() {
  const { locale } = useTranslation();
  const en = locale === 'en';

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 md:px-6 md:py-24">
      {/* Branding */}
      <div className="mb-8 flex items-center justify-center gap-2">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">FM</span>
          </div>
          <span className="text-lg font-bold text-text-primary">FlyMind</span>
        </Link>
      </div>

      {/* Heading */}
      <h1 className="text-center text-2xl font-bold text-text-primary md:text-3xl">
        {en ? 'Privacy Policy' : 'Informativa sulla Privacy'}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-center text-sm text-text-secondary md:text-base">
        {en
          ? 'How we collect, use and protect your personal data.'
          : 'Come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali.'}
      </p>

      {/* Content */}
      <Card padding="lg" className="mt-8">
        <div className="space-y-8 text-sm leading-relaxed text-text-secondary md:text-base">
          {/* 1. Titolare del trattamento */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '1. Data Controller' : '1. Titolare del trattamento'}
            </h2>
            <p>
              {en
                ? 'The data controller is Flymind Travel, based in Rome, Italy. For any privacy-related inquiries, you can contact us at: '
                : 'Il titolare del trattamento dei dati è Flymind Travel, con sede a Roma, Italia. Per qualsiasi richiesta relativa alla privacy, è possibile contattarci all\'indirizzo: '}
              <a href="mailto:privacy@flymind.travel" className="text-primary-600 hover:underline">
                privacy@flymind.travel
              </a>
            </p>
          </section>

          {/* 2. Dati raccolti */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '2. Data Collected' : '2. Dati raccolti'}
            </h2>
            <p>
              {en
                ? 'We collect the following categories of personal data:'
                : 'Raccogliamo le seguenti categorie di dati personali:'}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>{en ? 'Name and email address' : 'Nome e indirizzo email'}</li>
              <li>{en ? 'Password (stored as a cryptographic hash)' : 'Password (conservata come hash crittografico)'}</li>
              <li>{en ? 'Browsing and usage data' : 'Dati di navigazione e utilizzo'}</li>
              <li>{en ? 'Flight monitoring preferences' : 'Preferenze di monitoraggio voli'}</li>
              <li>
                {en
                  ? 'Payment data (processed and stored by Stripe; we do not store card details)'
                  : 'Dati di pagamento (gestiti e conservati da Stripe; non memorizziamo i dati della carta)'}
              </li>
            </ul>
          </section>

          {/* 3. Finalità del trattamento */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '3. Purpose of Processing' : '3. Finalità del trattamento'}
            </h2>
            <p>
              {en
                ? 'Your personal data is processed for the following purposes:'
                : 'I tuoi dati personali sono trattati per le seguenti finalità:'}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>{en ? 'Providing and maintaining the service' : 'Erogazione e mantenimento del servizio'}</li>
              <li>{en ? 'Flight price monitoring and alerts' : 'Monitoraggio dei prezzi dei voli e invio di avvisi'}</li>
              <li>{en ? 'Sending notifications (email, push, in-app)' : 'Invio di notifiche (email, push, in-app)'}</li>
              <li>{en ? 'Subscription and billing management' : 'Gestione degli abbonamenti e della fatturazione'}</li>
              <li>{en ? 'Service improvement and analytics' : 'Miglioramento del servizio e analisi'}</li>
              <li>{en ? 'Service-related communications' : 'Comunicazioni di servizio'}</li>
            </ul>
          </section>

          {/* 4. Base giuridica */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '4. Legal Basis' : '4. Base giuridica'}
            </h2>
            <p>
              {en
                ? 'We process your personal data based on the following legal grounds under the GDPR:'
                : 'Trattiamo i tuoi dati personali in base alle seguenti basi giuridiche previste dal GDPR:'}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <strong>{en ? 'User consent' : 'Consenso dell\'utente'}</strong>
                {en
                  ? ' — provided at the time of registration and when enabling optional features.'
                  : ' — fornito al momento della registrazione e all\'attivazione di funzionalità opzionali.'}
              </li>
              <li>
                <strong>{en ? 'Contractual performance' : 'Esecuzione contrattuale'}</strong>
                {en
                  ? ' — necessary to provide the services you have requested.'
                  : ' — necessario per fornire i servizi da te richiesti.'}
              </li>
              <li>
                <strong>{en ? 'Legitimate interest' : 'Legittimo interesse'}</strong>
                {en
                  ? ' — for service improvement, security, and fraud prevention.'
                  : ' — per il miglioramento del servizio, la sicurezza e la prevenzione delle frodi.'}
              </li>
            </ul>
          </section>

          {/* 5. Condivisione dati */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '5. Data Sharing' : '5. Condivisione dei dati'}
            </h2>
            <p>
              {en
                ? 'We share your data only with trusted third-party providers necessary to deliver the service:'
                : 'Condividiamo i tuoi dati esclusivamente con fornitori terzi di fiducia necessari all\'erogazione del servizio:'}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li><strong>Stripe</strong> — {en ? 'Payment processing' : 'Elaborazione dei pagamenti'}</li>
              <li><strong>Brevo</strong> — {en ? 'Transactional and service emails' : 'Email transazionali e di servizio'}</li>
              <li><strong>OneSignal</strong> — {en ? 'Push notifications' : 'Notifiche push'}</li>
              <li><strong>Cloudflare</strong> — {en ? 'Hosting and content delivery' : 'Hosting e distribuzione dei contenuti'}</li>
              <li><strong>Clerk</strong> — {en ? 'Social authentication' : 'Autenticazione sociale'}</li>
              <li><strong>Travelpayouts</strong> — {en ? 'Flight search and affiliate services' : 'Ricerca voli e servizi di affiliazione'}</li>
            </ul>
            <p className="mt-3 font-medium text-text-primary">
              {en
                ? 'We do not sell your personal data to third parties.'
                : 'Non vendiamo i tuoi dati personali a terzi.'}
            </p>
          </section>

          {/* 6. Cookie e tecnologie di tracciamento */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '6. Cookies and Tracking Technologies' : '6. Cookie e tecnologie di tracciamento'}
            </h2>
            <p>
              {en
                ? 'FlyMind uses the following technologies:'
                : 'FlyMind utilizza le seguenti tecnologie:'}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <strong>{en ? 'Technical cookies' : 'Cookie tecnici'}</strong>
                {en
                  ? ' — essential for authentication and core functionality.'
                  : ' — essenziali per l\'autenticazione e le funzionalità principali.'}
              </li>
              <li>
                <strong>localStorage</strong>
                {en
                  ? ' — used to manage your session and language preferences.'
                  : ' — utilizzato per gestire la sessione e le preferenze di lingua.'}
              </li>
              <li>
                <strong>{en ? 'Travelpayouts tracking script' : 'Script di tracciamento Travelpayouts'}</strong>
                {en
                  ? ' — used to attribute affiliate commissions on flight bookings.'
                  : ' — utilizzato per l\'attribuzione delle commissioni affiliate sulle prenotazioni dei voli.'}
              </li>
            </ul>
          </section>

          {/* 7. Conservazione dei dati */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '7. Data Retention' : '7. Conservazione dei dati'}
            </h2>
            <p>
              {en
                ? 'Your personal data is retained for the duration of your active account. Upon account deletion, all personal data will be permanently removed within 30 days.'
                : 'I tuoi dati personali sono conservati per tutta la durata del tuo account attivo. Alla cancellazione dell\'account, tutti i dati personali verranno eliminati definitivamente entro 30 giorni.'}
            </p>
          </section>

          {/* 8. Diritti dell'utente (GDPR) */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '8. Your Rights (GDPR)' : '8. Diritti dell\'utente (GDPR)'}
            </h2>
            <p>
              {en
                ? 'Under the General Data Protection Regulation (GDPR), you have the following rights:'
                : 'Ai sensi del Regolamento Generale sulla Protezione dei Dati (GDPR), hai i seguenti diritti:'}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li><strong>{en ? 'Access' : 'Accesso'}</strong> — {en ? 'request a copy of your personal data.' : 'richiedere una copia dei tuoi dati personali.'}</li>
              <li><strong>{en ? 'Rectification' : 'Rettifica'}</strong> — {en ? 'correct inaccurate or incomplete data.' : 'correggere dati inesatti o incompleti.'}</li>
              <li><strong>{en ? 'Erasure' : 'Cancellazione'}</strong> — {en ? 'request deletion of your personal data.' : 'richiedere la cancellazione dei tuoi dati personali.'}</li>
              <li><strong>{en ? 'Portability' : 'Portabilità'}</strong> — {en ? 'receive your data in a structured, machine-readable format.' : 'ricevere i tuoi dati in un formato strutturato e leggibile da dispositivo automatico.'}</li>
              <li><strong>{en ? 'Objection' : 'Opposizione'}</strong> — {en ? 'object to processing based on legitimate interest.' : 'opporsi al trattamento basato sul legittimo interesse.'}</li>
              <li><strong>{en ? 'Restriction' : 'Limitazione'}</strong> — {en ? 'request restricted processing of your data.' : 'richiedere la limitazione del trattamento dei tuoi dati.'}</li>
            </ul>
            <p className="mt-3">
              {en
                ? 'You can exercise these rights by contacting us at '
                : 'Puoi esercitare questi diritti contattandoci all\'indirizzo '}
              <a href="mailto:privacy@flymind.travel" className="text-primary-600 hover:underline">
                privacy@flymind.travel
              </a>
              {en
                ? ' or through the Settings > Privacy section of the app.'
                : ' oppure tramite la sezione Impostazioni > Privacy dell\'app.'}
            </p>
          </section>

          {/* 9. Trasferimento dati extra-UE */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '9. International Data Transfers' : '9. Trasferimento dati extra-UE'}
            </h2>
            <p>
              {en
                ? 'Some of our service providers (including Cloudflare and Stripe) may transfer your data outside the European Union. These transfers are protected by appropriate safeguards, including Standard Contractual Clauses (SCCs) approved by the European Commission.'
                : 'Alcuni dei nostri fornitori di servizi (tra cui Cloudflare e Stripe) possono trasferire i tuoi dati al di fuori dell\'Unione Europea. Tali trasferimenti sono protetti da garanzie adeguate, incluse le Clausole Contrattuali Standard (SCC) approvate dalla Commissione Europea.'}
            </p>
          </section>

          {/* 10. Modifiche */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '10. Changes to This Policy' : '10. Modifiche alla presente informativa'}
            </h2>
            <p>
              {en
                ? 'We reserve the right to update this privacy policy at any time. We will notify users of significant changes through the app or via email. Last updated: April 2026.'
                : 'Ci riserviamo il diritto di aggiornare questa informativa sulla privacy in qualsiasi momento. Notificheremo gli utenti in caso di modifiche significative tramite l\'app o via email. Ultimo aggiornamento: aprile 2026.'}
            </p>
          </section>

          {/* 11. Contatti */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-primary">
              {en ? '11. Contact' : '11. Contatti'}
            </h2>
            <p>
              {en
                ? 'For any questions regarding this privacy policy or the processing of your personal data, please contact us at: '
                : 'Per qualsiasi domanda riguardante questa informativa sulla privacy o il trattamento dei tuoi dati personali, contattaci all\'indirizzo: '}
              <a href="mailto:privacy@flymind.travel" className="text-primary-600 hover:underline">
                privacy@flymind.travel
              </a>
            </p>
          </section>
        </div>
      </Card>

      {/* Back link */}
      <div className="mt-8 text-center">
        <Link to="/" className="text-sm text-primary-600 hover:underline">
          {en ? 'Back to home' : 'Torna alla home'}
        </Link>
      </div>
    </div>
  );
}
